// ============================================================================
// Safe Care (Assistance Sociale) - Services API
// Module : features/assistance_sociale
//
// Suit le pattern existant : axios + Crypter.encrypt/decrypt + headers chiffrés.
// Les URLs sont construites à partir des contrôleurs backend :
//   - ProduitAssistance/*  -> ProduitAssistanceController
//   - Souscription/*        -> SouscriptionController
//   - Assistance/*          -> AssistanceController
// ============================================================================

import axios from 'axios';
import { Crypter } from '../../../Functions/Security/Crypter';
import { useAuthHeader } from '../../../Services/Auth/sessionAuth';
import {
  ProduitAssistance,
  VersionProduitAssistance,
  RegleEligibilite,
  RegleCotisation,
  EvenementCouvert,
  RegleEvenement,
  Plafond,
  RegleUtilisation,
  RegleCarence,
  RegleInterruption,
  RegleRupture,
  ParamNotifSafeCare,
  ParamFraisSafeCare,
  ApercuFraisSafeCare,
  DemandeSouscription,
  SouscriptionSafeCare,
  PersonneCouverte,
  EcheanceAssistance,
  Rachat,
  DemandeAssistance,
  ContexteValidationDemande,
  JustificatifDemande,
  DocumentRequis,
  DemandeSuspension,
  DocumentSuspension,
  CatalogueProduit,
  CatalogueEvenement,
  SafeCareResponse,
} from '../types';
import Data from '../../../Data/Data.json';
import FormatSMS from '../../../Models/FormatSMS';
// URL de base - réutilise la même que axiosInstance.ts
const BASE_URL= Data.safeCare.baseUrl;


// Helper pour décoder la réponse chiffrée
function decodeResponse<T>(encryptedData: string): SafeCareResponse<T> {
  return JSON.parse(Crypter.decrypt(encryptedData)).Value;
}

// Helper pour chiffrer un payload
function encryptPayload<T>(data: T): string {
  return Crypter.encrypt(JSON.stringify(data));
}

// Helper pour chiffrer une valeur simple (string, number)
function encryptValue<T>(value: T): string {
  return Crypter.encrypt(String(value));
}

// ==================== PRODUITASSISTANCE ====================
export const produitAssistanceApi = (authHeader: string) => ({
  createProduit: async (produit: ProduitAssistance): Promise<SafeCareResponse> => {
    const data = encryptPayload(produit);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/CreateProduit`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  updateProduit: async (produit: ProduitAssistance): Promise<SafeCareResponse> => {
    const data = encryptPayload(produit);
    const res = await axios.put(`${BASE_URL}/ProduitAssistance/UpdateProduit`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getAllProduits: async (): Promise<SafeCareResponse<ProduitAssistance>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetAllProduits`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getProduitById: async (idProduit: number): Promise<SafeCareResponse<ProduitAssistance>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetProduitById`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdProduit: encryptValue(idProduit),
      },
    });
    return decodeResponse(res.data);
  },

  deleteProduit: async (idProduit: number): Promise<SafeCareResponse> => {
    const res = await axios.delete(`${BASE_URL}/ProduitAssistance/DeleteProduit`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdProduit: encryptValue(idProduit),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== VERSION ====================
  createVersion: async (version: VersionProduitAssistance): Promise<SafeCareResponse> => {
    const data = encryptPayload(version);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/CreateVersion`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  updateVersion: async (version: VersionProduitAssistance): Promise<SafeCareResponse> => {
    const data = encryptPayload(version);
    const res = await axios.put(`${BASE_URL}/ProduitAssistance/UpdateVersion`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  publierVersion: async (idVersion: number, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/PublierVersion`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  getVersionsByProduit: async (idProduit: number): Promise<SafeCareResponse<VersionProduitAssistance>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetVersionsByProduit`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdProduit: encryptValue(idProduit),
      },
    });
    return decodeResponse(res.data);
  },

  getPublishedVersion: async (idProduit: number): Promise<SafeCareResponse<VersionProduitAssistance>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetPublishedVersion`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdProduit: encryptValue(idProduit),
      },
    });
    return decodeResponse(res.data);
  },

  getComptesClientByPlans: async (numcli: string, idVersion: number): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetComptesClientByPlans`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherNumcli: encryptValue(numcli),
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== REGLES LIEES A UNE VERSION ====================
  saveEligibilite: async (regle: RegleEligibilite): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveEligibilite`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getEligibilite: async (idVersion: number): Promise<SafeCareResponse<RegleEligibilite>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetEligibilite`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  saveCotisation: async (regle: RegleCotisation): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveCotisation`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getCotisation: async (idVersion: number): Promise<SafeCareResponse<RegleCotisation>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetCotisation`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== COMPTES DE CONTREPARTIE (COMPTESOPERAT) ====================
  // Contrôle des comptes paramétrés pour un code opération dans une agence.
  // typeOperation : COTISATION (Σ quotes-parts = 100 %) | INDEMNISATION (un seul compte)
  controleComptesOperat: async (codOpe: string, codAge: string, typeOperation: 'COTISATION' | 'INDEMNISATION'): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/ControleComptesOperat`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherCodOpe: encryptValue(codOpe),
        cypherCodAge: encryptValue(codAge),
        cypherTypeOperation: encryptValue(typeOperation),
      },
    });
    return decodeResponse(res.data);
  },

  saveCarence: async (regle: RegleCarence): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveCarence`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getCarence: async (idVersion: number): Promise<SafeCareResponse<RegleCarence>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetCarence`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  deleteCarence: async (idRegleCarence: number): Promise<SafeCareResponse> => {
    const res = await axios.delete(`${BASE_URL}/ProduitAssistance/DeleteCarence`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdRegleCarence: encryptValue(idRegleCarence),
      },
    });
    return decodeResponse(res.data);
  },

  saveInterruption: async (regle: RegleInterruption): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveInterruption`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getInterruption: async (idVersion: number): Promise<SafeCareResponse<RegleInterruption>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetInterruption`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  // Paramètres généraux de notification SMS (défauts ; la version prime)
  saveParamNotif: async (param: ParamNotifSafeCare): Promise<SafeCareResponse> => {
    const data = encryptPayload(param);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveParamNotif`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getParamNotif: async (): Promise<SafeCareResponse<ParamNotifSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetParamNotif`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // Codes opération globaux des frais Safe Care (communs à tous les produits)
  saveParamFrais: async (param: ParamFraisSafeCare): Promise<SafeCareResponse> => {
    const data = encryptPayload(param);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveParamFrais`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getParamFrais: async (): Promise<SafeCareResponse<ParamFraisSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetParamFrais`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // Aperçu du frais (grille du code opération sur la cotisation de la version) :
  // idVersion, sinon version publiée de idProduit, sinon version de idSouscription
  apercuFrais: async (
    typeFrais: 'SOUSCRIPTION' | 'REACTIVATION',
    ids: { idVersion?: number; idProduit?: number; idSouscription?: number }
  ): Promise<SafeCareResponse<ApercuFraisSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/ApercuFrais`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherTypeFrais: encryptValue(typeFrais),
        ...(ids.idVersion ? { cypherIdVersion: encryptValue(String(ids.idVersion)) } : {}),
        ...(ids.idProduit ? { cypherIdProduit: encryptValue(String(ids.idProduit)) } : {}),
        ...(ids.idSouscription ? { cypherIdSouscription: encryptValue(String(ids.idSouscription)) } : {}),
      },
    });
    return decodeResponse(res.data);
  },

  saveRupture: async (regle: RegleRupture): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveRupture`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getRupture: async (idVersion: number): Promise<SafeCareResponse<RegleRupture>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetRupture`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  getRupturesAutoEnCours: async (): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetRupturesAutoEnCours`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  suspendreRuptureAuto: async (idSouscription: number, motif: string, utiVal: string): Promise<SafeCareResponse> => {
    const data = encryptPayload({ IdSouscription: idSouscription, Motif: motif, UtiVal: utiVal });
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SuspendreRuptureAuto`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // ==================== EVENEMENTS COUVERTS ====================
  createEvenement: async (evenement: EvenementCouvert): Promise<SafeCareResponse> => {
    const data = encryptPayload(evenement);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/CreateEvenement`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  updateEvenement: async (evenement: EvenementCouvert): Promise<SafeCareResponse> => {
    const data = encryptPayload(evenement);
    const res = await axios.put(`${BASE_URL}/ProduitAssistance/UpdateEvenement`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getEvenementsByVersion: async (idVersion: number): Promise<SafeCareResponse<EvenementCouvert>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetEvenementsByVersion`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
      },
    });
    return decodeResponse(res.data);
  },

  deleteEvenement: async (idEvenement: number): Promise<SafeCareResponse> => {
    const res = await axios.delete(`${BASE_URL}/ProduitAssistance/DeleteEvenement`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdEvenement: encryptValue(idEvenement),
      },
    });
    return decodeResponse(res.data);
  },

  saveRegleEvenement: async (regle: RegleEvenement): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveRegleEvenement`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getRegleEvenement: async (idEvenement: number): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetRegleEvenement`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdEvenement: encryptValue(idEvenement),
      },
    });
    return decodeResponse(res.data);
  },

  savePlafond: async (plafond: Plafond): Promise<SafeCareResponse> => {
    const data = encryptPayload(plafond);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SavePlafond`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  saveUtilisation: async (regle: RegleUtilisation): Promise<SafeCareResponse> => {
    const data = encryptPayload(regle);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveUtilisation`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // ==================== DOCUMENTS REQUIS PAR VERSION (admin) ====================
  getDocumentsRequisByVersion: async (idVersion: number, contexte?: string): Promise<SafeCareResponse<DocumentRequis>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetDocumentsRequisByVersion`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdVersion: encryptValue(idVersion),
        ...(contexte ? { cypherContexte: encryptValue(contexte) } : {}),
      },
    });
    return decodeResponse(res.data);
  },

  saveDocumentRequis: async (document: DocumentRequis): Promise<SafeCareResponse> => {
    const data = encryptPayload(document);
    const res = await axios.post(`${BASE_URL}/ProduitAssistance/SaveDocumentRequis`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  deleteDocumentRequis: async (idDocumentRequis: number): Promise<SafeCareResponse> => {
    const res = await axios.delete(`${BASE_URL}/ProduitAssistance/DeleteDocumentRequis`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDocumentRequis: encryptValue(idDocumentRequis),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== CATALOGUE SAFE CARE ====================
  getCatalogueProduits: async (): Promise<SafeCareResponse<CatalogueProduit>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetCatalogueProduits`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getCatalogueEvenements: async (categorie?: string): Promise<SafeCareResponse<CatalogueEvenement>> => {
    const res = await axios.get(`${BASE_URL}/ProduitAssistance/GetCatalogueEvenements`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        ...(categorie ? { cypherCategorie: encryptValue(categorie) } : {}),
      },
    });
    return decodeResponse(res.data);
  },
});

// ==================== FORMATSMS (gabarits SMS — référentiel AnyBank partagé) ====================
// Routes du FormatSMSController ([Route("[controller]")]) adossées à
// sp_AnyManageFormatSMS. CypherSearch attend une chaîne JSON "search|searchBy"
// (searchBy : CODE / LIBELLE / ALL — vide = ALL).
export const formatSmsApi = (authHeader: string) => ({
  getAll: async (search?: string): Promise<SafeCareResponse<FormatSMS>> => {
    const res = await axios.get(`${BASE_URL}/FormatSMS/GetFormatSMS`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        CypherSearch: encryptPayload(search ?? ''),
      },
    });
    return decodeResponse(res.data);
  },

  create: async (gabarit: FormatSMS): Promise<SafeCareResponse> => {
    const data = encryptPayload(gabarit);
    const res = await axios.post(`${BASE_URL}/FormatSMS/CreateFormatSMS`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  update: async (gabarit: FormatSMS): Promise<SafeCareResponse> => {
    const data = encryptPayload(gabarit);
    const res = await axios.put(`${BASE_URL}/FormatSMS/UpdateFormatSMS`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },
});

// ==================== SOUSCRIPTION ====================
export const souscriptionApi = (authHeader: string) => ({
  createDemandeSouscription: async (demande: DemandeSouscription): Promise<SafeCareResponse> => {
    const data = encryptPayload(demande);
    const res = await axios.post(`${BASE_URL}/Souscription/CreateDemandeSouscription`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getAllDemandesSouscription: async (statut?: string): Promise<SafeCareResponse<DemandeSouscription>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetAllDemandesSouscription`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherStatut: encryptValue(statut ?? ''),
      },
    });
    return decodeResponse(res.data);
  },

  getDemandeSouscriptionById: async (idDemande: number): Promise<SafeCareResponse<DemandeSouscription>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetDemandeSouscriptionById`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemande: encryptValue(idDemande),
      },
    });
    return decodeResponse(res.data);
  },

  validerDemandeSouscription: async (idDemande: number, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/ValiderDemandeSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemande: encryptValue(idDemande),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  rejeterDemandeSouscription: async (idDemande: number, motifRejet: string, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/RejeterDemandeSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemande: encryptValue(idDemande),
        cypherMotifRejet: encryptValue(motifRejet),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  annulerDemandeSouscription: async (idDemande: number, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/AnnulerDemandeSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemande: encryptValue(idDemande),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  remettreDemandeSouscriptionEnValidation: async (idDemande: number, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/RemettreDemandeSouscriptionEnValidation`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemande: encryptValue(idDemande),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  getSouscriptionsByClient: async (numcli: string): Promise<SafeCareResponse<SouscriptionSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetSouscriptionsByClient`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherNumcli: encryptValue(numcli),
      },
    });
    return decodeResponse(res.data);
  },

  getAllSouscriptions: async (statut?: string): Promise<SafeCareResponse<SouscriptionSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetAllSouscriptions`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherStatut: encryptValue(statut ?? ''),
      },
    });
    return decodeResponse(res.data);
  },

  getStatistiques: async (dateDebut?: string, dateFin?: string, idProduit?: number): Promise<SafeCareResponse> => {
    const headers: Record<string, string> = { Authorization: authHeader, 'Content-Type': 'application/json' };
    if (dateDebut) headers.cypherDateDebut = encryptValue(dateDebut);
    if (dateFin) headers.cypherDateFin = encryptValue(dateFin);
    if (idProduit) headers.cypherIdProduit = encryptValue(idProduit);
    const res = await axios.get(`${BASE_URL}/Souscription/GetStatistiques`, { headers });
    return decodeResponse(res.data);
  },

  addPersonneCouverte: async (personne: PersonneCouverte): Promise<SafeCareResponse> => {
    const data = encryptPayload(personne);
    const res = await axios.post(`${BASE_URL}/Souscription/AddPersonneCouverte`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getPersonnesCouvertes: async (idSouscription: number): Promise<SafeCareResponse<PersonneCouverte>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetPersonnesCouvertes`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
      },
    });
    return decodeResponse(res.data);
  },

  deletePersonneCouverte: async (idPersonneCouverte: number): Promise<SafeCareResponse> => {
    const res = await axios.delete(`${BASE_URL}/Souscription/DeletePersonneCouverte`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdPersonneCouverte: encryptValue(idPersonneCouverte),
      },
    });
    return decodeResponse(res.data);
  },

  genererEcheances: async (idSouscription: number, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/GenererEcheances`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  getEcheancesBySouscription: async (idSouscription: number): Promise<SafeCareResponse<EcheanceAssistance>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetEcheancesBySouscription`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
      },
    });
    return decodeResponse(res.data);
  },

  preleverEcheance: async (idEcheance: number, codope: string, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/PreleverEcheance`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdEcheance: encryptValue(idEcheance),
        cypherCodope: encryptValue(codope),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  createRachat: async (rachat: Rachat): Promise<SafeCareResponse> => {
    const data = encryptPayload(rachat);
    const res = await axios.post(`${BASE_URL}/Souscription/CreateRachat`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // ==================== INTERRUPTION / SUSPENSION / RESILIATION ====================
  getSouscriptionsImpayees: async (): Promise<SafeCareResponse<SouscriptionSafeCare>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetSouscriptionsImpayees`, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  suspendreSouscription: async (idSouscription: number, motif: string, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/SuspendreSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherMotif: encryptValue(motif),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  addSuspensionDocument: async (idSouscription: number, description: string, pieceJointe: string, nomFichier: string, utiSai: string, idDocumentRequis?: number): Promise<SafeCareResponse> => {
    const data = encryptPayload({ IdSouscription: idSouscription, Description: description, PieceJointe: pieceJointe, NomFichier: nomFichier, UtiSai: utiSai, IdDocumentRequis: idDocumentRequis });
    const res = await axios.post(`${BASE_URL}/Souscription/AddSuspensionDocument`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // ==================== DEMANDES DE SUSPENSION (flux demande -> validation) ====================
  demanderSuspension: async (idSouscription: number, motif: string, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/DemanderSuspension`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherMotif: encryptValue(motif),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  addDemandeSuspensionDocument: async (idDemandeSuspension: number, description: string, pieceJointe: string, nomFichier: string, utiSai: string, idDocumentRequis?: number): Promise<SafeCareResponse> => {
    const data = encryptPayload({ IdDemandeSuspension: idDemandeSuspension, Description: description, PieceJointe: pieceJointe, NomFichier: nomFichier, UtiSai: utiSai, IdDocumentRequis: idDocumentRequis });
    const res = await axios.post(`${BASE_URL}/Souscription/AddDemandeSuspensionDocument`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  // Demande de réactivation : TypeDemande='REACTIVATION' sur DEMANDESUSPENSION
  // (souscription SUSPENDUE -> demande EN_ATTENTE -> validation sur /assurance/demande/suspension-active)
  demanderReactivation: async (idSouscription: number, motif: string, utiSai: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/DemanderReactivation`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherMotif: encryptValue(motif),
        cypherUtiSai: encryptValue(utiSai),
      },
    });
    return decodeResponse(res.data);
  },

  getDemandesSuspension: async (statut?: string, typeDemande?: string): Promise<SafeCareResponse<DemandeSuspension>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetDemandesSuspension`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherStatut: encryptValue(statut ?? ''),
        cypherTypeDemande: encryptValue(typeDemande ?? ''),
      },
    });
    return decodeResponse(res.data);
  },

  getDocumentsDemandeSuspension: async (idDemandeSuspension: number): Promise<SafeCareResponse<DocumentSuspension>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetDocumentsDemandeSuspension`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeSuspension: encryptValue(idDemandeSuspension),
      },
    });
    return decodeResponse(res.data);
  },

  validerDemandeSuspension: async (idDemandeSuspension: number, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/ValiderDemandeSuspension`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeSuspension: encryptValue(idDemandeSuspension),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  rejeterDemandeSuspension: async (idDemandeSuspension: number, motifRejet: string, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/RejeterDemandeSuspension`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeSuspension: encryptValue(idDemandeSuspension),
        cypherMotifRejet: encryptValue(motifRejet),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  reactiverSouscription: async (idSouscription: number, motif: string, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/ReactiverSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherMotif: encryptValue(motif),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  resilierSouscription: async (idSouscription: number, motif: string, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/ResilierSouscription`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherMotif: encryptValue(motif),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  marquerEcheanceImpayee: async (idEcheance: number, utiVal: string): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/MarquerEcheanceImpayee`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdEcheance: encryptValue(idEcheance),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== CONTRÔLE AUTOMATIQUE DES IMPAYÉS ====================
  controlerImpayes: async (): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/ControlerImpayes`, null, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  marquerEcheancesImpayees: async (): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/MarquerEcheancesImpayees`, null, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  cloturerContratsEchus: async (): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Souscription/CloturerContratsEchus`, null, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getEcheancesImpayeesBySouscription: async (idSouscription: number): Promise<SafeCareResponse<EcheanceAssistance>> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetEcheancesImpayeesBySouscription`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
      },
    });
    return decodeResponse(res.data);
  },

  getHistoriqueInterruption: async (idSouscription: number): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/Souscription/GetHistoriqueInterruption`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
      },
    });
    return decodeResponse(res.data);
  },
});

// ==================== ASSISTANCE ====================
export const assistanceApi = (authHeader: string) => ({
  createDemandeAssistance: async (demande: DemandeAssistance): Promise<SafeCareResponse> => {
    const data = encryptPayload(demande);
    const res = await axios.post(`${BASE_URL}/Assistance/CreateDemandeAssistance`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getAllDemandesAssistance: async (statut?: string): Promise<SafeCareResponse<DemandeAssistance>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetAllDemandesAssistance`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherStatut: encryptValue(statut ?? ''),
      },
    });
    return decodeResponse(res.data);
  },

  getDemandeAssistanceById: async (idDemandeAssistance: number): Promise<SafeCareResponse<DemandeAssistance>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetDemandeAssistanceById`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
      },
    });
    return decodeResponse(res.data);
  },

  getContexteValidation: async (idDemandeAssistance: number): Promise<SafeCareResponse<ContexteValidationDemande>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetContexteValidation`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
      },
    });
    return decodeResponse(res.data);
  },

  validerDemandeAssistance: async (
    idDemandeAssistance: number,
    montantIndemnisation: number,
    codope: string,
    utiVal: string
  ): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Assistance/ValiderDemandeAssistance`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
        cypherMontantIndemnisation: encryptValue(montantIndemnisation),
        cypherCodope: encryptValue(codope),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  rejeterDemandeAssistance: async (
    idDemandeAssistance: number,
    motifRejet: string,
    utiVal: string
  ): Promise<SafeCareResponse> => {
    const res = await axios.post(`${BASE_URL}/Assistance/RejeterDemandeAssistance`, null, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
        cypherMotifRejet: encryptValue(motifRejet),
        cypherUtiVal: encryptValue(utiVal),
      },
    });
    return decodeResponse(res.data);
  },

  addJustificatif: async (justificatif: JustificatifDemande): Promise<SafeCareResponse> => {
    const data = encryptPayload(justificatif);
    const res = await axios.post(`${BASE_URL}/Assistance/AddJustificatif`, data, {
      headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
    });
    return decodeResponse(res.data);
  },

  getJustificatifsByDemande: async (idDemandeAssistance: number): Promise<SafeCareResponse<JustificatifDemande>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetJustificatifsByDemande`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
      },
    });
    return decodeResponse(res.data);
  },

  // ==================== DOCUMENTS REQUIS (côté client) ====================
  getDocumentsRequisBySouscription: async (idSouscription: number, contexte: string = 'INDEMNISATION', idEvenement?: number): Promise<SafeCareResponse<DocumentRequis>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetDocumentsRequisBySouscription`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdSouscription: encryptValue(idSouscription),
        cypherContexte: encryptValue(contexte),
        ...(idEvenement ? { cypherIdEvenement: encryptValue(idEvenement) } : {}),
      },
    });
    return decodeResponse(res.data);
  },

  getDocumentsRequisManquants: async (idDemandeAssistance: number): Promise<SafeCareResponse<DocumentRequis>> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetDocumentsRequisManquants`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherIdDemandeAssistance: encryptValue(idDemandeAssistance),
      },
    });
    return decodeResponse(res.data);
  },

  getHistorique: async (typeEntite: string, idEntite: number): Promise<SafeCareResponse> => {
    const res = await axios.get(`${BASE_URL}/Assistance/GetHistorique`, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        cypherTypeEntite: encryptValue(typeEntite),
        cypherIdEntite: encryptValue(idEntite),
      },
    });
    return decodeResponse(res.data);
  },
});

// Hook factory pour utiliser les APIs avec le header d'authentification
export function useSafeCareApi() {
  const authHeader = useAuthHeader();
  return {
    produit: produitAssistanceApi(authHeader()),
    souscription: souscriptionApi(authHeader()),
    assistance: assistanceApi(authHeader()),
    formatSms: formatSmsApi(authHeader()),
  };
}
